import { hasInventoryCountPermission, hasPurchasingStockPermission } from "../utils/purchasingStockPermissions";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useSearchParams } from "react-router-dom";
import {
  FiBarChart2,
  FiClipboard,
  FiChevronDown,
  FiGrid,
  FiKey,
  FiLogOut,
  FiMapPin,
  FiMenu,
  FiPackage,
  FiShield,
  FiShoppingCart,
  FiTrendingUp,
  FiTruck,
  FiTool,
  FiUsers,
  FiX,
} from "react-icons/fi";
import NavbarPurchasing from "../Components/NavbarPurchasing";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useIsMobile from "../hooks/useIsMobile";
import axios from "../api/axiosInstance";
import { readStoredUser, setActiveBusinessUnitSession } from "../utils/sessionUser";
import { hasPurchasingSettingsPermission, hasPurchasingApprovalSettingsAccess } from "../utils/purchasingSettingsPermissions";
import "./PurchasingDashboardShell.css";

const PURCHASING_NAV_ES = {
  Dashboard: "Resumen",
  Summary: "Vista general",
  "Stock Orders": "Pedidos de stock",
  "Service Orders": "Servicios",
  "Service requisitions": "Requisiciones de servicio",
  "Service orders": "Órdenes de servicio",
  "Service confirmations": "Confirmaciones de servicio",
  "Requests, orders & completion": "Requisiciones, órdenes y ejecución",
  "Service requests": "Solicitudes de servicios",
  "Service approvals": "Aprobaciones de servicios",
  "Service departments": "Departamentos de servicios",
  "Requests & quotations": "Solicitudes y cotizaciones",
  "Requisitions & receivings": "Solicitudes y recepciones",
  "Purchase Requisitions": "Solicitudes de compra",
  "Purchase Orders": "Órdenes de compra",
  Approvals: "Aprobaciones",
  "Pending approvals": "Pendientes de aprobación",
  "Requests & history": "Solicitudes e histórico",
  Receivings: "Recepciones",
  "Supplier returns": "Devoluciones a proveedor",
  "Delivery Notes": "Albaranes",
  "Supplier Invoices": "Facturas de proveedores",
  "Invoice review": "Revisión de facturas",
  "Stock Management": "Inventario",
  "Stock Movements": "Movimientos de existencias",
  Manufacturing: "Fabricación",
  Stocktaking: "Recuentos de inventario",
  "Requests, returns & transfers": "Solicitudes, devoluciones y traspasos",
  "Counts & snapshots": "Recuentos y cierres",
  "Inventory control": "Control de existencias",
  "Inventory Intelligence": "Análisis del inventario",
  "Current Stock": "Existencias actuales",
  "Snapshot Ledger": "Historial entre fechas",
  "Stock Requisitions": "Solicitudes de material",
  "Stock Returns": "Devoluciones de stock",
  "Stock Transfers": "Traspasos de existencias",
  "Requisition approvals": "Aprobaciones de solicitudes",
  "Transfer approvals": "Aprobaciones de traspasos",
  "Inventory Count": "Recuento de inventario",
  Suppliers: "Proveedores",
  Directory: "Directorio",
  "Items & Families": "Artículos y familias",
  Tenders: "Licitaciones",
  "Catalog tree": "Organización del catálogo",
  Items: "Artículos",
  Subcategories: "Subcategorías",
  Categories: "Categorías",
  Subfamilies: "Subfamilias",
  "Item catalog": "Catálogo de artículos",
  "POS items": "Artículos del POS",
  "Equivalent products": "Productos equivalentes",
  "Equivalent product comparison": "Comparativa de productos equivalentes",
  Families: "Familias",
  "Recipes & Costing": "Recetas y costes",
  Recipes: "Recetas",
  "Menu & Margins": "Carta y márgenes",
  "Production costs": "Costes de producción",
  Reports: "Informes",
  "Purchasing analysis": "Análisis de compras",
  "Items Purchased": "Artículos comprados",
  "Item last purchase": "Última compra por artículo",
  "Last unit cost": "Último coste unitario",
  Settings: "Configuración",
  Configuration: "Preferencias del módulo",
  "Units of Measure": "Unidades de medida",
  "Company Info": "Datos de la empresa",
  "User Access Rules": "Reglas de acceso",
  "Users & Roles": "Usuarios y roles",
  "User Experience": "Experiencia de usuario",
  "Approvals & Permissions": "Aprobaciones y permisos",
  "Operations closure": "Cierre de operaciones",
  "Inventory Locations": "Ubicaciones de inventario",
  "WhatsApp senders": "Remitentes de WhatsApp",
  Purchasing: "Compras",
  "Purchasing Panel": "Inventarios y compras",
  "Purchasing navigation": "Navegación de inventarios y compras",
  "Business Unit": "Unidad de negocio",
  Logout: "Cerrar sesión",
  "Pin sidebar": "Fijar menú lateral",
  "Unpin sidebar": "Desfijar menú lateral",
  "Open navigation": "Abrir navegación",
  "Close navigation": "Cerrar navegación",
  "Switching business unit...": "Cambiando de unidad de negocio…",
  "Could not switch business unit.": "No se ha podido cambiar de unidad de negocio.",
  "You don't have permission to access the Purchasing module.":
    "No tienes permiso para acceder al módulo de inventarios y compras.",
};

const SERVICE_NAV_CA = {
  "Service Orders": "Serveis", "Service requisitions": "Requisicions de servei",
  "Service orders": "Ordres de servei", "Service confirmations": "Confirmacions de servei",
  "Requests, orders & completion": "Requisicions, ordres i execució",
};

function PurchasingPanel() {
  const location = useLocation();
  const { themeId, languageId } = usePmsTheme();
  const isApprovalsWorkspace = /^\/purchasing\/(?:approvals|catalog-approvals)\/?$/.test(location.pathname);
  const isDashboardWorkspace = /^\/purchasing\/dashboard\/?$/.test(location.pathname);
  const isServiceOrdersWorkspace = /^\/purchasing\/service-orders(?:\/(?:requisitions|orders|confirmations|approvals))?\/?$/.test(location.pathname);
  const isSupplierInvoicesWorkspace = /^\/purchasing\/supplier-invoices\/?$/.test(location.pathname);
  const isOrdersWorkspace = /^\/purchasing\/orders(?:\/(?:requisitions|purchase-orders|approvals|receivings|supplier-returns|delivery-notes))?\/?$/.test(location.pathname);
  const isInventoryIntelligenceWorkspace = /^\/purchasing\/stock-management\/(?:inventory-intelligence|current-stock|snapshot-report)\/?$/.test(location.pathname);
  const isInventoryIntelligenceAlias = /^\/purchasing\/stock-management\/inventory-intelligence\/?$/.test(location.pathname);
  const isStockDispatchedAlias = /^\/purchasing\/stock-management\/dispatched\/?$/.test(location.pathname);
  const isInventoryCountWorkspace = /^\/purchasing\/stock-management\/inventory-count\/?$/.test(location.pathname);
  const isStockRequisitionsWorkspace = /^\/purchasing\/stock-management\/(?:requisitions(?:\/approvals)?|dispatched|returns|manufacturing|transfers(?:\/approvals)?)\/?$/.test(location.pathname);
  const isSettingsWorkspace = /^\/purchasing\/settings(?:\/(?:company|units|access|users|appearance|orders|services|locations|whatsapp|operation-closures))?\/?$/.test(location.pathname);
  const isPosCatalogWorkspace = /^\/purchasing\/items\/pos-catalog\/?$/.test(location.pathname);
  const isEquivalentCatalogWorkspace = /^\/purchasing\/items\/equivalent-products\/?$/.test(location.pathname);
  const isCatalogWorkspace = /^\/purchasing\/items(?:\/(?:items|families|subfamilies|categories|subcategories|pos-catalog|equivalent-products))?\/?$/.test(location.pathname);
  const isSuppliersWorkspace = /^\/purchasing\/suppliers\/?$/.test(location.pathname);
  const isRecipeLibraryWorkspace = /^\/purchasing\/recipes\/?$/.test(location.pathname);
  const isRecipesWorkspace = /^\/purchasing\/recipes(?:\/menu)?\/?$/.test(location.pathname);
  const isDirectoryWorkspace = isCatalogWorkspace || isSuppliersWorkspace || isRecipesWorkspace;
  const isReportsWorkspace = /^\/purchasing\/reports(?:\/(?:items-purchased|item-last-purchase|item-last-unit-cost|equivalent-products))?\/?$/.test(location.pathname);
  const isPilcoreWorkspace = /^\/purchasing\/(?:dashboard|items\/tenders)\/?$/.test(location.pathname) || isOrdersWorkspace || isServiceOrdersWorkspace || isSupplierInvoicesWorkspace || isInventoryIntelligenceWorkspace || isInventoryCountWorkspace || isStockRequisitionsWorkspace || isSettingsWorkspace || isDirectoryWorkspace || isReportsWorkspace || isApprovalsWorkspace;
  const shellText = (text) =>
    languageId === "es" ? PURCHASING_NAV_ES[text] || text : languageId === "ca" && SERVICE_NAV_CA[text] ? SERVICE_NAV_CA[text] : languageId === "ca" && text === "Supplier Invoices" ? "Factures de proveïdors" : languageId === "ca" && text === "Invoice review" ? "Revisió de factures" : languageId === "ca" && text === "Approvals" ? "Aprovacions" : languageId === "ca" && text === "Requests & history" ? "Sol·licituds i històric" : languageId === "ca" && text === "Pending approvals" ? "Pendents d’aprovació" : text;
  const [searchParams] = useSearchParams();
  const [openSection, setOpenSection] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarPinned, setIsSidebarPinned] = useState(false);
  const isMobile = useIsMobile();
  const [businessUnitName, setBusinessUnitName] = useState("");
  const [sessionUser, setSessionUser] = useState(() => readStoredUser());
  const [isSyncingBusinessUnit, setIsSyncingBusinessUnit] = useState(false);
  const [businessUnitSyncError, setBusinessUnitSyncError] = useState("");
  const navScrollRef = useRef(null);
  const sectionRefs = useRef(new Map());

  const currentUser = sessionUser || {};
  const signedInUserName = typeof currentUser.name === "string" ? currentUser.name.trim() : "";
  const permissions = currentUser?.permissions || {};
  const isAdminRole = Number(currentUser?.id) === 1;

  const can = (key) => isAdminRole || Boolean(permissions[key]);

  const businessUnitId =
    searchParams.get("business_unit_id") ||
    currentUser?.active_business_unit_id ||
    "1";
  const requestedBusinessUnitId = Number(businessUnitId || 0);
  const activeBusinessUnitId = Number(currentUser?.active_business_unit_id || 0);
  const isBusinessUnitTokenStale =
    Boolean(currentUser?.id) &&
    Boolean(requestedBusinessUnitId) &&
    Boolean(activeBusinessUnitId) &&
    requestedBusinessUnitId !== activeBusinessUnitId;

  const withBusinessUnit = useCallback(
    (path) => `${path}?business_unit_id=${encodeURIComponent(String(businessUnitId))}`,
    [businessUnitId]
  );

  const canRequestServices = can("purchasing.services.requests");
  const canManageServiceOrders = can("purchasing.services.orders");
  const canConfirmServices = can("purchasing.services.confirmations");
  const canApproveServices = can("purchasing.services.approvals");
  const canConfigureServices = can("purchasing.services.configure");
  const canViewOrders = can("purchasing.orders.view_tab");
  const canCreateMarketplace = isAdminRole || permissions["purchasing.requisitions.create"] === true;
  const canViewRequisitions = can("purchasing.requisitions.view_tab");
  const canViewReceivings = can("purchasing.receivings.view_tab");
  const canViewDeliveryNotes = can("purchasing.delivery_notes.view_tab");
  const canViewSupplierInvoices = can("purchasing.supplier_invoices.view_tab");

  const canViewCurrentStock = hasPurchasingStockPermission(currentUser, "purchasing.stock.current");
  const canViewStockReports = hasPurchasingStockPermission(currentUser, "purchasing.stock.current.snapshot_report") ||
    hasPurchasingStockPermission(currentUser, "purchasing.stock.current.accounting_closures.view");
  const canViewStockRequisitions = hasPurchasingStockPermission(currentUser, "purchasing.stock.requisitions");
  const canViewStockDispatched = hasPurchasingStockPermission(currentUser, "purchasing.stock.dispatched");
  const canViewStockReturns = hasPurchasingStockPermission(currentUser, "purchasing.stock.returns");
  const canViewInventoryCount = hasInventoryCountPermission(currentUser, "view");
  const canViewStockTransfers = hasPurchasingStockPermission(currentUser, "purchasing.stock.transfers");
  const canViewSuppliers = isAdminRole || permissions["purchasing.suppliers.view"] === true;
  const canViewItems = isAdminRole || permissions["purchasing.items.view"] === true;
  const canViewTenders = can("purchasing.tenders.view") || can("access_settings");
  const canViewCategories = isAdminRole || permissions["purchasing.categories.view"] === true;
  const canViewEquivalents = isAdminRole || permissions["purchasing.equivalents.view"] === true;
  const canViewRecipes = isAdminRole || permissions["purchasing.recipes.view"] === true;
  const canViewMenu = isAdminRole || permissions["purchasing.menu.view"] === true;
  const canViewItemsPurchasedReport = isAdminRole || permissions["purchasing.reports.items_purchased.view"] === true;
  const canViewSettings = can("purchasing.settings.view");
  const canViewCompanySettings = hasPurchasingSettingsPermission(currentUser, "company", "view");
  const canViewUnitsSettings = hasPurchasingSettingsPermission(currentUser, "units", "view");
  const canViewLocationsSettings = hasPurchasingSettingsPermission(currentUser, "locations", "view");
  const canViewOperationClosures = hasPurchasingSettingsPermission(currentUser, "operation_closures", "view");
  const canViewWhatsAppSettings = hasPurchasingSettingsPermission(currentUser, "whatsapp", "view");
  const canViewAccessRules = hasPurchasingSettingsPermission(currentUser, "access", "view");
  const canViewApprovalSettings = hasPurchasingApprovalSettingsAccess(currentUser);
  const canViewUsersSettings = isAdminRole || permissions["hotel_settings.view_users_tab"] === true;

  const canAccessPurchasing =
    (Array.isArray(currentUser.allowed_modules) && currentUser.allowed_modules.includes("purchasing")) ||
    isAdminRole ||
    can("access_settings") ||
    Object.keys(permissions).some((k) => k.startsWith("purchasing.") && permissions[k]);
  const canAccessApprovals = Boolean(currentUser.id && requestedBusinessUnitId && requestedBusinessUnitId === activeBusinessUnitId && canAccessPurchasing);

  const isSidebarExpanded = isSidebarPinned || isSidebarOpen;

  const handleSidebarEnter = () => {
    if (!isSidebarExpanded) {
      setOpenSection(null);
    }
    setIsSidebarOpen(true);
  };

  const handleSidebarLeave = () => {
    if (isSidebarPinned) return;
    setIsSidebarOpen(false);
    setOpenSection(null);
  };

  const handlePinToggle = () => {
    setIsSidebarPinned((prev) => {
      const next = !prev;
      if (next) {
        if (!isSidebarExpanded) {
          setOpenSection(null);
        }
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
        setOpenSection(null);
      }
      return next;
    });
  };

  const setSectionRef = useCallback((key, node) => {
    if (node) {
      sectionRefs.current.set(key, node);
      return;
    }
    sectionRefs.current.delete(key);
  }, []);

  useEffect(() => {
    const syncSessionFromStorage = () => {
      setSessionUser(readStoredUser());
    };

    window.addEventListener("palantir-session-updated", syncSessionFromStorage);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionFromStorage);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const syncActiveBusinessUnit = async () => {
      const requestedBu = Number(businessUnitId || 0);
      const activeBu = Number(currentUser?.active_business_unit_id || 0);

      if (!currentUser?.id || !requestedBu || requestedBu === activeBu) {
        setIsSyncingBusinessUnit(false);
        setBusinessUnitSyncError("");
        return;
      }

      try {
        setIsSyncingBusinessUnit(true);
        setBusinessUnitSyncError("");
        const nextUser = await setActiveBusinessUnitSession(currentUser.id, requestedBu);
        if (!cancelled) {
          setSessionUser(nextUser);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("[PurchasingPanel] Error syncing active business unit:", err);
          setBusinessUnitSyncError("Could not switch business unit.");
        }
      } finally {
        if (!cancelled) {
          setIsSyncingBusinessUnit(false);
        }
      }
    };

    syncActiveBusinessUnit();

    return () => {
      cancelled = true;
    };
  }, [businessUnitId, currentUser?.active_business_unit_id, currentUser?.id]);

  useEffect(() => {
    let cancelled = false;

    const loadBusinessUnitName = async () => {
      if (!businessUnitId) return;
      try {
        const res = await axios.get(`/api/business-units/${businessUnitId}`);
        if (!cancelled) {
          setBusinessUnitName(String(res?.data?.name || "").trim());
        }
      } catch (err) {
        if (!cancelled) {
          setBusinessUnitName("");
        }
        console.warn("[PurchasingPanel] Error loading business unit name:", err);
      }
    };

    loadBusinessUnitName();

    return () => {
      cancelled = true;
    };
  }, [businessUnitId]);

  useEffect(() => {
    if (!isSidebarExpanded || !openSection) {
      return undefined;
    }

    const navElement = navScrollRef.current;
    const sectionElement = sectionRefs.current.get(openSection);

    if (!navElement || !sectionElement) {
      return undefined;
    }

    let frameId = 0;
    let timeoutId = 0;

    const ensureSectionVisible = () => {
      if (!navScrollRef.current || !sectionRefs.current.has(openSection)) {
        return;
      }

      const currentNav = navScrollRef.current;
      const currentSection = sectionRefs.current.get(openSection);

      if (!currentNav || !currentSection) {
        return;
      }

      const navRect = currentNav.getBoundingClientRect();
      const sectionRect = currentSection.getBoundingClientRect();
      const safePadding = 12;
      const overflowBottom = sectionRect.bottom - navRect.bottom;
      const overflowTop = navRect.top - sectionRect.top;

      if (overflowBottom > -safePadding) {
        currentNav.scrollTo({
          top: currentNav.scrollTop + overflowBottom + safePadding,
          behavior: "smooth",
        });
        return;
      }

      if (overflowTop > safePadding) {
        currentNav.scrollTo({
          top: Math.max(0, currentNav.scrollTop - overflowTop - safePadding),
          behavior: "smooth",
        });
      }
    };

    frameId = window.requestAnimationFrame(() => {
      ensureSectionVisible();
      timeoutId = window.setTimeout(ensureSectionVisible, 170);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [isSidebarExpanded, openSection]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("palantir_token");
    localStorage.removeItem("palantir_user");
    window.location.href = "/";
  }, []);

  const uiCss = `
    .purchShell {
      min-height: 100vh;
      width: 100%;
      display: flex;
      gap: 16px;
      padding: 14px;
      background:
        radial-gradient(1100px 520px at 8% -10%, rgba(48, 216, 255, 0.10), transparent 62%),
        radial-gradient(900px 520px at 90% 0%, rgba(168, 85, 247, 0.12), transparent 58%),
        radial-gradient(1100px 680px at 40% 110%, rgba(255, 77, 79, 0.07), transparent 62%);
    }

    @media (max-width: 768px) {
      .purchShell {
        padding: 10px;
        gap: 10px;
      }
    }

    .purchSidebar {
      position: sticky;
      top: 14px;
      height: calc(100vh - 28px);
      width: 96px;
      flex: 0 0 auto;
      border-radius: 18px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
        linear-gradient(135deg, rgba(48, 216, 255, 0.55), rgba(168, 85, 247, 0.65), rgba(255, 77, 79, 0.40)) border-box;
      box-shadow: 0 28px 80px rgba(0, 0, 0, 0.55);
      overflow: hidden;
      transition: width 160ms ease;
    }

    .purchSidebar::before {
      content: "";
      position: absolute;
      inset: 0;
      background:
        radial-gradient(520px 220px at 20% 0%, rgba(48, 216, 255, 0.12), transparent 60%),
        radial-gradient(420px 220px at 90% 10%, rgba(168, 85, 247, 0.14), transparent 62%);
      pointer-events: none;
      opacity: 0.95;
    }

    .purchSidebar > * {
      position: relative;
      z-index: 1;
    }

    .purchSidebar--open {
      width: 280px;
    }

    .purchSidebar:not(.purchSidebar--open) .purchNavItemText {
      display: none;
    }

    .purchSidebar:not(.purchSidebar--open) .purchChevron {
      display: none;
    }

    .purchSidebar:not(.purchSidebar--open) .purchBrand {
      gap: 0;
      justify-content: center;
      padding-left: 0;
      padding-right: 0;
    }

    .purchSidebar:not(.purchSidebar--open) .purchBrandText {
      display: none;
    }

    .purchSidebar:not(.purchSidebar--open) .purchBrandMark {
      margin: 0 auto;
    }

    .purchSidebar:not(.purchSidebar--open) .purchNav {
      width: calc(100% + 8px);
      margin-right: -8px;
      padding-left: 0;
      padding-right: 0;
    }

    .purchSidebar:not(.purchSidebar--open) .purchNav > * {
      width: calc(100% - 8px);
    }

    .purchSidebar:not(.purchSidebar--open) .purchNavItem {
      justify-content: center;
      padding-left: 0;
      padding-right: 0;
    }

    .purchSidebar:not(.purchSidebar--open) .purchNavItemIcon {
      margin: 0 auto;
    }

    .purchSidebarInner {
      height: 100%;
      display: flex;
      flex-direction: column;
      padding: 12px 8px;
      gap: 10px;
      overflow: hidden;
    }

    .purchBrand {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 8px;
      border-radius: 14px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      background: rgba(255, 255, 255, 0.03);
      user-select: none;
    }

    .purchBrandMark {
      width: 40px;
      height: 40px;
      min-width: 40px;
      min-height: 40px;
      flex: 0 0 40px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      padding: 6px;
      border: 1px solid rgba(48, 216, 255, 0.35);
      background: radial-gradient(circle at 20% 20%, rgba(48, 216, 255, 0.18), rgba(168, 85, 247, 0.20));
      color: rgba(230, 230, 247, 0.92);
      font-weight: 900;
      letter-spacing: 0.4px;
      box-shadow: 0 18px 40px rgba(48, 216, 255, 0.08);
    }

    .purchBrandLogo {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.38));
    }

    .purchBrandText {
      display: flex;
      flex-direction: column;
      gap: 1px;
      overflow: hidden;
      opacity: 0;
      max-width: 0;
      transition: opacity 160ms ease, max-width 160ms ease;
    }

    .purchSidebar--open .purchBrandText {
      opacity: 1;
      max-width: 195px;
    }

    .purchBrandTitle {
      font-weight: 900;
      color: transparent;
      background: linear-gradient(
        90deg,
        #e6e6f7 0%,
        rgba(48, 216, 255, 0.95) 45%,
        rgba(168, 85, 247, 0.95) 95%
      );
      -webkit-background-clip: text;
      background-clip: text;
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .purchBrandSubtitle {
      font-size: 0.72rem;
      color: rgba(230, 230, 247, 0.55);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .purchNav {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      gap: 6px;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;
      padding-top: 2px;
      padding-right: 4px;
      padding-bottom: 8px;
      scrollbar-width: thin;
      scrollbar-color: rgba(230, 230, 247, 0.16) transparent;
      scroll-behavior: smooth;
      overscroll-behavior: contain;
    }

    .purchNav::-webkit-scrollbar {
      width: 4px;
    }

    .purchNav::-webkit-scrollbar-track {
      background: transparent;
    }

    .purchNav::-webkit-scrollbar-thumb {
      background: rgba(230, 230, 247, 0.16);
      border-radius: 999px;
    }

    .purchNav:hover::-webkit-scrollbar-thumb {
      background: rgba(230, 230, 247, 0.24);
    }

    .purchNavSection {
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    .purchNavItem {
      width: 100%;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.02);
      color: rgba(230, 230, 247, 0.88);
      text-decoration: none;
      cursor: pointer;
      transition: transform 120ms ease, background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 8px;
      padding: 10px 8px;
      text-align: left;
    }

    .purchNavItem:hover {
      background: rgba(48, 216, 255, 0.06);
      border-color: rgba(48, 216, 255, 0.18);
      transform: translateY(-1px);
      box-shadow: 0 18px 40px rgba(0, 0, 0, 0.25);
    }

    .purchNavItem--active {
      background: rgba(168, 85, 247, 0.12);
      border-color: rgba(168, 85, 247, 0.25);
      box-shadow: 0 18px 50px rgba(168, 85, 247, 0.10);
    }

    .purchNavItemIcon {
      width: 40px;
      height: 40px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.10);
      flex: 0 0 auto;
      color: rgba(230, 230, 247, 0.92);
      box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
    }

    .purchNavItem--active .purchNavItemIcon {
      border-color: rgba(48, 216, 255, 0.28);
      background: radial-gradient(circle at 20% 20%, rgba(48, 216, 255, 0.14), rgba(168, 85, 247, 0.16));
      box-shadow: 0 18px 40px rgba(48, 216, 255, 0.08);
    }

    .purchNavItemText {
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;
      flex: 1 1 auto;
    }

    .purchNavItemLabel,
    .purchNavItemMeta,
    .purchChevron {
      opacity: 0;
      max-width: 0;
      overflow: hidden;
      transition: opacity 160ms ease, max-width 160ms ease, transform 160ms ease;
    }

    .purchNavItemLabel {
      font-weight: 850;
      font-size: 0.95rem;
      color: rgba(230, 230, 247, 0.92);
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .purchNavItemMeta {
      font-size: 0.74rem;
      color: rgba(230, 230, 247, 0.55);
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .purchChevron {
      margin-left: auto;
      color: rgba(230, 230, 247, 0.55);
      display: grid;
      place-items: center;
    }

    .purchNavItem[aria-expanded="true"] .purchChevron {
      transform: rotate(180deg);
    }

    .purchSidebar--open .purchNavItemLabel,
    .purchSidebar--open .purchNavItemMeta,
    .purchSidebar--open .purchChevron {
      opacity: 1;
      max-width: 240px;
    }

    .purchSidebar--open .purchChevron {
      max-width: 28px;
    }

    .purchSubNav {
      margin-left: 8px;
      margin-right: 6px;
      border-left: 1px solid rgba(255, 255, 255, 0.08);
      padding-left: 12px;
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      transition: max-height 180ms ease, opacity 180ms ease;
    }

    .purchSubNav--open {
      max-height: 960px;
      overflow: visible;
      opacity: 1;
      padding-top: 6px;
      padding-bottom: 8px;
    }

    .purchSidebar:not(.purchSidebar--open) .purchSubNav {
      max-height: 0;
      opacity: 0;
      padding-top: 0;
      padding-bottom: 0;
      pointer-events: none;
    }

    .purchSubItem {
      display: flex;
      align-items: center;
      gap: 10px;
      border-radius: 12px;
      padding: 9px 10px;
      margin: 4px 0;
      color: rgba(230, 230, 247, 0.82);
      text-decoration: none;
      border: 1px solid rgba(255, 255, 255, 0.06);
      background: rgba(255, 255, 255, 0.02);
      transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
    }

    .purchSubItem:hover {
      background: rgba(48, 216, 255, 0.05);
      border-color: rgba(48, 216, 255, 0.16);
      transform: translateY(-1px);
    }

    .purchSubItem--active {
      background: rgba(48, 216, 255, 0.08);
      border-color: rgba(48, 216, 255, 0.22);
      color: rgba(230, 230, 247, 0.92);
    }

    .purchBrandHotel {
      font-size: 0.67rem;
      color: rgba(206, 219, 243, 0.54);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 1px;
      line-height: 1.15;
    }

    .purchPinButton {
      margin-left: auto;
      width: 28px;
      height: 28px;
      border-radius: 10px;
      border: 1px solid rgba(255, 255, 255, 0.12);
      background: rgba(255, 255, 255, 0.04);
      color: rgba(230, 230, 247, 0.75);
      display: grid;
      place-items: center;
      cursor: pointer;
      transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
    }

    .purchPinButton:hover {
      background: rgba(48, 216, 255, 0.1);
      border-color: rgba(48, 216, 255, 0.28);
      color: rgba(230, 230, 247, 0.95);
    }

    .purchPinButton--active {
      background: rgba(168, 85, 247, 0.18);
      border-color: rgba(168, 85, 247, 0.4);
      color: rgba(230, 230, 247, 0.98);
    }

    .purchMain {
      flex: 1 1 auto;
      min-width: 0;
      padding: 0 4px;
      height: calc(100dvh - 28px);
      overflow: clip;
    }

    .purchMain,
    .purchMain * {
      scrollbar-width: thin;
      scrollbar-color: rgba(109, 208, 235, 0.72) rgba(255, 255, 255, 0.08);
    }

    .purchMain *::-webkit-scrollbar {
      width: 7px;
      height: 7px;
    }

    .purchMain *::-webkit-scrollbar-thumb {
      background: linear-gradient(
        180deg,
        rgba(109, 208, 235, 0.9),
        rgba(37, 154, 211, 0.9)
      );
      border-radius: 999px;
    }

    .purchMain *::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.08);
      border-radius: 999px;
    }

  `;

  const menuItems = useMemo(() => {
    const ordersChildren = [
      canCreateMarketplace && { key: "marketplace", label: "Marketplace", to: withBusinessUnit("/purchasing/orders/marketplace") },
      canViewRequisitions && {
        key: "requisitions",
        label: "Purchase Requisitions",
        to: withBusinessUnit("/purchasing/orders/requisitions"),
      },
      canViewOrders && {
        key: "purchase-orders",
        label: "Purchase Orders",
        to: withBusinessUnit("/purchasing/orders/purchase-orders"),
      },
      canViewReceivings && {
        key: "receivings",
        label: "Receivings",
        to: withBusinessUnit("/purchasing/orders/receivings"),
      },
      (canViewReceivings || canViewDeliveryNotes) && {
        key: "supplier-returns",
        label: "Supplier returns",
        to: withBusinessUnit("/purchasing/orders/supplier-returns"),
      },
      canViewDeliveryNotes && {
        key: "delivery-notes",
        label: "Delivery Notes",
        to: withBusinessUnit("/purchasing/orders/delivery-notes"),
      },
    ].filter(Boolean);

    const stockChildren = [
      canViewCurrentStock && {
        key: "current-stock",
        label: "Current Stock",
        to: withBusinessUnit("/purchasing/stock-management/current-stock"),
      },
      canViewStockReports && {
        key: "snapshot-report",
        label: "Snapshot Ledger",
        to: withBusinessUnit("/purchasing/stock-management/snapshot-report"),
      },
    ].filter(Boolean);

    const stockMovementChildren = [
      canViewStockRequisitions && {
        key: "manufacturing",
        label: "Manufacturing",
        to: withBusinessUnit("/purchasing/stock-management/manufacturing"),
      },
      (canViewStockRequisitions || canViewStockDispatched) && {
        key: "stock-requisitions",
        label: "Stock Requisitions",
        to: withBusinessUnit("/purchasing/stock-management/requisitions"),
      },
      canViewStockReturns && {
        key: "stock-returns",
        label: "Stock Returns",
        to: withBusinessUnit("/purchasing/stock-management/returns"),
      },
      canViewStockTransfers && {
        key: "stock-transfer",
        label: "Stock Transfers",
        to: withBusinessUnit("/purchasing/stock-management/transfers"),
      },
    ].filter(Boolean);

    const stocktakingChildren = [
      canViewInventoryCount && {
        key: "inventory-count",
        label: "Inventory Count",
        to: withBusinessUnit("/purchasing/stock-management/inventory-count"),
      },
    ].filter(Boolean);

    const itemChildren = [
      (canViewItems || canViewCategories) && {
        key: "catalog",
        label: "Item catalog",
        to: withBusinessUnit("/purchasing/items"),
      },
      canViewEquivalents && { key: "equivalent-products", label: "Equivalent products", to: withBusinessUnit("/purchasing/items/equivalent-products") },
      canViewTenders && {
        key: "tenders",
        label: "Tenders",
        to: withBusinessUnit("/purchasing/items/tenders"),
      },
      canViewItems && {
        key: "pos-catalog",
        label: "POS items",
        to: withBusinessUnit("/purchasing/items/pos-catalog"),
      },
    ].filter(Boolean);

    const settingsChildren = [
      canViewCompanySettings && {
        key: "company",
        label: "Company Info",
        to: withBusinessUnit("/purchasing/settings/company"),
      },
      canViewUnitsSettings && {
        key: "units",
        label: "Units of Measure",
        to: withBusinessUnit("/purchasing/settings/units"),
      },
      canViewAccessRules && {
        key: "access",
        label: "User Access Rules",
        to: withBusinessUnit("/purchasing/settings/access"),
      },
      canViewUsersSettings && {
        key: "users",
        label: "Users & Roles",
        to: withBusinessUnit("/purchasing/settings/users"),
      },
      canViewSettings && {
        key: "appearance",
        label: "User Experience",
        to: withBusinessUnit("/purchasing/settings/appearance"),
      },
      (canViewApprovalSettings || canConfigureServices) && {
        key: "orders",
        label: "Approvals & Permissions",
        to: withBusinessUnit("/purchasing/settings/orders"),
      },
      canConfigureServices && {
        key: "service-configuration",
        label: "Service departments",
        to: withBusinessUnit("/purchasing/settings/services"),
      },
      canViewLocationsSettings && {
        key: "locations",
        label: "Inventory Locations",
        to: withBusinessUnit("/purchasing/settings/locations"),
      },
      canViewOperationClosures && {
        key: "operation-closures",
        label: "Operations closure",
        to: withBusinessUnit("/purchasing/settings/operation-closures"),
      },
      canViewWhatsAppSettings && {
        key: "whatsapp",
        label: "WhatsApp senders",
        to: withBusinessUnit("/purchasing/settings/whatsapp"),
      },
    ].filter(Boolean);

    const reportChildren = [
      canViewItemsPurchasedReport && { key: "equivalent-products", label: "Equivalent product comparison", to: withBusinessUnit("/purchasing/reports/equivalent-products") },
      canViewItemsPurchasedReport && {
        key: "items-purchased",
        label: "Items Purchased",
        to: withBusinessUnit("/purchasing/reports/items-purchased"),
      },
      canViewItemsPurchasedReport && {
        key: "item-last-purchase",
        label: "Item last purchase",
        to: withBusinessUnit("/purchasing/reports/item-last-purchase"),
      },
      canViewItemsPurchasedReport && {
        key: "item-last-unit-cost",
        label: "Last unit cost",
        to: withBusinessUnit("/purchasing/reports/item-last-unit-cost"),
      },
    ].filter(Boolean);

    return [
      {
        key: "dashboard",
        label: "Dashboard",
        meta: "Summary",
        to: withBusinessUnit("/purchasing/dashboard"),
        Icon: FiTrendingUp,
      },
      canAccessApprovals ? {
        key: "approvals",
        label: "Approvals",
        meta: "Requests & history",
        to: withBusinessUnit("/purchasing/approvals"),
        Icon: FiShield,
      } : null,
      ordersChildren.length
        ? {
            key: "orders",
            label: "Stock Orders",
            meta: "Requisitions & receivings",
            activePrefix: "/purchasing/orders",
            Icon: FiShoppingCart,
            children: ordersChildren,
          }
        : null,
      canViewSupplierInvoices ? {
        key: "supplier-invoices",
        label: "Supplier Invoices",
        meta: "Invoice review",
        to: withBusinessUnit("/purchasing/supplier-invoices"),
        Icon: FiClipboard,
      } : null,
      (canRequestServices || canManageServiceOrders || canApproveServices || canConfirmServices) ? {
        key: "service-orders",
        label: "Service Orders",
        meta: "Requests, orders & completion",
        activePrefix: "/purchasing/service-orders",
        Icon: FiTool,
        children: [
          (canRequestServices || canManageServiceOrders) && { key: "service-requests", label: "Service requisitions", to: withBusinessUnit("/purchasing/service-orders/requisitions") },
          { key: "service-order-list", label: "Service orders", to: withBusinessUnit("/purchasing/service-orders/orders") },
          (canRequestServices || canManageServiceOrders || canConfirmServices) && { key: "service-confirmations", label: "Service confirmations", to: withBusinessUnit("/purchasing/service-orders/confirmations") },
        ].filter(Boolean),
      } : null,
      stockChildren.length
        ? {
            key: "stock-management",
            label: "Stock Management",
            meta: "Inventory control",
            activePattern: /^\/purchasing\/stock-management(?:\/(?:current-stock|snapshot-report|inventory-intelligence))?\/?$/,
            Icon: FiPackage,
            children: stockChildren,
          }
        : null,
      stockMovementChildren.length
        ? {
            key: "stock-movements",
            label: "Stock Movements",
            meta: "Requests, returns & transfers",
            activePattern: /^\/purchasing\/stock-management\/(?:manufacturing|requisitions(?:\/approvals)?|returns|transfers(?:\/approvals)?|dispatched)\/?$/,
            Icon: FiTruck,
            children: stockMovementChildren,
          }
        : null,
      stocktakingChildren.length
        ? {
            key: "stocktaking",
            label: "Stocktaking",
            meta: "Counts & snapshots",
            activePattern: /^\/purchasing\/stock-management\/inventory-count\/?$/,
            Icon: FiClipboard,
            children: stocktakingChildren,
          }
        : null,
      canViewSuppliers
        ? {
            key: "suppliers",
            label: "Suppliers",
            meta: "Directory",
            to: withBusinessUnit("/purchasing/suppliers"),
            Icon: FiUsers,
          }
        : null,
      itemChildren.length
        ? {
            key: "items",
            label: "Items & Families",
            meta: "Catalog tree",
            activePrefix: "/purchasing/items",
            Icon: FiGrid,
            children: itemChildren,
          }
        : null,
      (canViewRecipes || canViewMenu)
        ? {
            key: "recipes",
            label: "Recipes & Costing",
            meta: "Production costs",
            activePrefix: "/purchasing/recipes",
            Icon: FiKey,
            children: [
              canViewRecipes && { key: "recipe-library", label: "Recipes", to: withBusinessUnit("/purchasing/recipes") },
              canViewMenu && { key: "menu-margins", label: "Menu & Margins", to: withBusinessUnit("/purchasing/recipes/menu") },
            ].filter(Boolean),
          }
        : null,
      reportChildren.length
        ? {
            key: "reports",
            label: "Reports",
            meta: "Purchasing analysis",
            activePrefix: "/purchasing/reports",
            Icon: FiBarChart2,
            children: reportChildren,
          }
        : null,
      settingsChildren.length
        ? {
            key: "settings",
            label: "Settings",
            meta: "Configuration",
            activePrefix: "/purchasing/settings",
            Icon: FiShield,
            children: settingsChildren,
          }
        : null,
    ].filter(Boolean);
  }, [
    canRequestServices,
    canManageServiceOrders,
    canConfirmServices,
    canApproveServices,
    canConfigureServices,
    canAccessApprovals,
    canViewCategories,
    canViewEquivalents,
    canViewCurrentStock,
    canViewStockReports,
    canViewDeliveryNotes,
    canViewSupplierInvoices,
    canViewInventoryCount,
    canViewItems,
    canViewTenders,
    canViewOrders,
    canViewReceivings,
    canViewRecipes,
    canViewMenu,
    canViewItemsPurchasedReport,
    canCreateMarketplace,
    canViewRequisitions,
    canViewSettings,
    canViewCompanySettings,
    canViewUnitsSettings,
    canViewLocationsSettings,
    canViewWhatsAppSettings,
    canViewOperationClosures,
    canViewAccessRules,
    canViewApprovalSettings,
    canViewUsersSettings,
    canViewStockDispatched,
    canViewStockRequisitions,
    canViewStockTransfers,
    canViewStockReturns,
    canViewSuppliers,
    withBusinessUnit,
  ]);

  if (!canAccessPurchasing) {
    return (
      <div
        className={isPilcoreWorkspace ? "purchShell--dashboard purchShellNotice" : undefined}
        data-theme={isPilcoreWorkspace ? themeId : undefined}
        role="alert"
        style={isPilcoreWorkspace ? undefined : {
          color: "white",
          textAlign: "center",
          marginTop: "4rem",
          fontSize: "1.2rem",
        }}
      >
        {shellText("You don't have permission to access the Purchasing module.")}
      </div>
    );
  }

  const outletContent = businessUnitSyncError ? (
    <div className="purchShellNotice" role="alert" style={isPilcoreWorkspace ? undefined : { color: "white", padding: "2rem", textAlign: "center" }}>
      {shellText(businessUnitSyncError)}
    </div>
  ) : isSyncingBusinessUnit || isBusinessUnitTokenStale ? (
    <div className="purchShellNotice" role="status" style={isPilcoreWorkspace ? undefined : { color: "white", padding: "2rem", textAlign: "center" }}>
      {shellText("Switching business unit...")}
    </div>
  ) : (
    <Outlet />
  );

  if (location.pathname === "/purchasing/orders/marketplace") return outletContent;

  if (isMobile) {
    if (isPilcoreWorkspace) {
      return (
        <div className={`purchShell purchShell--dashboard purchShell--mobile${isDashboardWorkspace ? " purchShell--overview" : ""}${isOrdersWorkspace || isServiceOrdersWorkspace || isApprovalsWorkspace ? " purchShell--orders" : ""}${isInventoryIntelligenceWorkspace ? " purchShell--intelligence" : ""}${isInventoryCountWorkspace ? " purchShell--counts" : ""}${isStockRequisitionsWorkspace ? " purchShell--stock-requisitions" : ""}${isSettingsWorkspace ? " purchShell--settings" : ""}${isReportsWorkspace ? " purchShell--reports" : ""}${isDirectoryWorkspace ? " purchShell--directory" : ""}`} data-theme={themeId}>
          <style>{uiCss}</style>
          <header className="purchMobileHeader">
            <img src={`${process.env.PUBLIC_URL}/Isotipo_Pilcore_Blanco.svg`} alt="Pilcore" />
            <div className="purchMobileBrand">
              <strong>{shellText("Purchasing Panel")}</strong>
              <span>{businessUnitName || currentUser?.business_unit_name || shellText("Business Unit")}</span>
            </div>
            <button
              type="button"
              className="purchMobileToggle"
              aria-label={shellText(isSidebarOpen ? "Close navigation" : "Open navigation")}
              aria-expanded={isSidebarOpen}
              aria-controls="purchDashboardMobileNav"
              onClick={() => setIsSidebarOpen((open) => !open)}
            >
              {isSidebarOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>
          </header>
          {isSidebarOpen ? (
            <nav
              id="purchDashboardMobileNav"
              className="purchMobileNav"
              aria-label={shellText("Purchasing navigation")}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setIsSidebarOpen(false);
                  event.currentTarget.parentElement.querySelector(".purchMobileToggle")?.focus();
                }
              }}
            >
              {menuItems.map((item) => item.children?.length ? (
                <section key={item.key} className="purchMobileNavGroup">
                  <h2>{shellText(item.label)}</h2>
                  {item.children.map((child) => (isInventoryIntelligenceAlias && child.key === "current-stock") || (isStockDispatchedAlias && child.key === "stock-requisitions") || (isCatalogWorkspace && !isPosCatalogWorkspace && !isEquivalentCatalogWorkspace && child.key === "catalog") || (isRecipeLibraryWorkspace && child.key === "recipe-library") ? (
                    <Link key={child.key} to={child.to} aria-current="page" onClick={() => setIsSidebarOpen(false)}>
                      {shellText(child.label)}
                    </Link>
                  ) : (
                    <NavLink key={child.key} to={child.to} end={["catalog", "stock-requisitions", "stock-transfer", "service-requests", "recipe-library"].includes(child.key)} onClick={() => setIsSidebarOpen(false)}>
                      {shellText(child.label)}
                    </NavLink>
                  ))}
                </section>
              ) : (
                <NavLink key={item.key} to={item.to} onClick={() => setIsSidebarOpen(false)}>
                  {shellText(item.label)}
                </NavLink>
              ))}
              <button type="button" className="purchMobileLogout" onClick={handleLogout}>
                <FiLogOut aria-hidden="true" /> {shellText("Logout")}
              </button>
            </nav>
          ) : null}
          <main className="purchMain purchMain--mobile">{outletContent}</main>
        </div>
      );
    }
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#000" }}>
        <style>{uiCss}</style>
        <NavbarPurchasing />
        <main
          className="purchMain purchMain--mobile"
          style={{ paddingTop: "64px", paddingLeft: 8, paddingRight: 8 }}
        >
          {outletContent}
        </main>
      </div>
    );
  }

  return (
    <div className={`purchShell${isPilcoreWorkspace ? " purchShell--dashboard" : ""}${isDashboardWorkspace ? " purchShell--overview" : ""}${isOrdersWorkspace || isServiceOrdersWorkspace || isApprovalsWorkspace ? " purchShell--orders" : ""}${isInventoryIntelligenceWorkspace ? " purchShell--intelligence" : ""}${isInventoryCountWorkspace ? " purchShell--counts" : ""}${isStockRequisitionsWorkspace ? " purchShell--stock-requisitions" : ""}${isSettingsWorkspace ? " purchShell--settings" : ""}${isReportsWorkspace ? " purchShell--reports" : ""}${isDirectoryWorkspace ? " purchShell--directory" : ""}`} data-theme={themeId}>
      <style>{uiCss}</style>

      <aside
        className={`purchSidebar ${isSidebarExpanded ? "purchSidebar--open" : ""} ${
          isSidebarPinned ? "purchSidebar--pinned" : ""
        }`}
        aria-label={shellText("Purchasing navigation")}
        onMouseEnter={handleSidebarEnter}
        onMouseLeave={handleSidebarLeave}
        onFocus={() => setIsSidebarOpen(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) handleSidebarLeave();
        }}
      >
        <div className="purchSidebarInner">
          <div className="purchBrand" title={shellText("Purchasing Panel")}>
            <div className="purchBrandMark">
              <img
                className="purchBrandLogo"
                src={`${process.env.PUBLIC_URL}/Isotipo_Pilcore_Blanco.svg`}
                alt="Pilcore"
              />
            </div>
            <div className="purchBrandText">
              <div className="purchBrandTitle">Pilcore</div>
              <div className="purchBrandSubtitle">{shellText("Purchasing")}</div>
              <div
                className="purchBrandHotel"
                title={businessUnitName || currentUser?.business_unit_name || shellText("Business Unit")}
              >
                {businessUnitName || currentUser?.business_unit_name || shellText("Business Unit")}
              </div>
            </div>
            {isSidebarExpanded ? (
              <button
                type="button"
                className={`purchPinButton ${isSidebarPinned ? "purchPinButton--active" : ""}`}
                onClick={handlePinToggle}
                title={shellText(isSidebarPinned ? "Unpin sidebar" : "Pin sidebar")}
                aria-pressed={isSidebarPinned}
                aria-label={shellText(isSidebarPinned ? "Unpin sidebar" : "Pin sidebar")}
              >
                <FiMapPin size={14} />
              </button>
            ) : null}
          </div>

          {signedInUserName ? (
            <div className="purchSignedInUser" title={signedInUserName}>
              {signedInUserName.split(/(\s+)/).map((part, index) => (
                /\s/.test(part) ? part : <span className="purchSignedInUserWord" key={index}>{part}</span>
              ))}
            </div>
          ) : null}

          <nav ref={navScrollRef} className="purchNav">
            {menuItems.map((item) => {
              if (item.children?.length) {
                const isOpen = openSection === item.key;
                const isActive = item.activePattern
                  ? item.activePattern.test(location.pathname)
                  : location.pathname.startsWith(item.activePrefix);
                const Icon = item.Icon;

                return (
                  <div
                    key={item.key}
                    ref={(node) => setSectionRef(item.key, node)}
                    className="purchNavSection"
                  >
                    <button
                      type="button"
                      className={`purchNavItem ${isActive ? "purchNavItem--active" : ""}`}
                      aria-expanded={isOpen}
                      onClick={() => setOpenSection((prev) => (prev === item.key ? null : item.key))}
                      title={shellText(item.label)}
                    >
                      <span className="purchNavItemIcon" aria-hidden="true">
                        <Icon size={18} />
                      </span>
                      <span className="purchNavItemText">
                        <span className="purchNavItemLabel">{shellText(item.label)}</span>
                        <span className="purchNavItemMeta">{shellText(item.meta)}</span>
                      </span>
                      <span className="purchChevron" aria-hidden="true">
                        <FiChevronDown size={16} />
                      </span>
                    </button>

                    <div
                      className={`purchSubNav ${isOpen ? "purchSubNav--open" : ""}`}
                      hidden={!isOpen}
                    >
                      {item.children.map((child) => (isInventoryIntelligenceAlias && child.key === "current-stock") || (isStockDispatchedAlias && child.key === "stock-requisitions") || (isCatalogWorkspace && !isPosCatalogWorkspace && !isEquivalentCatalogWorkspace && child.key === "catalog") || (isRecipeLibraryWorkspace && child.key === "recipe-library") ? (
                        <Link
                          key={child.key}
                          to={child.to}
                          title={shellText(child.label)}
                          className="purchSubItem purchSubItem--active"
                          aria-current="page"
                        >
                          {shellText(child.label)}
                        </Link>
                      ) : (
                        <NavLink
                          key={child.key}
                          to={child.to}
                          title={shellText(child.label)}
                          end={["catalog", "stock-requisitions", "stock-transfer", "service-requests", "recipe-library"].includes(child.key)}
                          className={({ isActive: subActive }) =>
                            `purchSubItem ${subActive ? "purchSubItem--active" : ""}`
                          }
                        >
                          {shellText(child.label)}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }

              const Icon = item.Icon;
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  title={shellText(item.label)}
                  className={({ isActive }) => `purchNavItem ${isActive ? "purchNavItem--active" : ""}`}
                >
                  <span className="purchNavItemIcon" aria-hidden="true">
                    <Icon size={18} />
                  </span>
                  <span className="purchNavItemText">
                    <span className="purchNavItemLabel">{shellText(item.label)}</span>
                    <span className="purchNavItemMeta">{shellText(item.meta)}</span>
                  </span>
                </NavLink>
              );
            })}
          </nav>
          <div className="purchSidebarFooter">
            <button
              type="button"
              className="purchSidebarLogout"
              onClick={handleLogout}
              title={shellText("Logout")}
              aria-label={shellText("Logout")}
            >
              <FiLogOut size={16} aria-hidden="true" />
              <span className="purchSidebarLogoutText">{shellText("Logout")}</span>
            </button>
          </div>
        </div>
      </aside>

      <main className="purchMain">
        {outletContent}
      </main>
    </div>
  );
}

export default PurchasingPanel;
